import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  TextField,
  Button,
  Typography,
  IconButton,
  Box,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { motion } from "framer-motion";

import SupportImage from "../../images/career-coach.jpg";

// Thin, icon-less field styling to match the V3 popup reference.
const inputStyle = {
  background: "#ffffff",
  "& .MuiOutlinedInput-root": {
    borderRadius: "9px",
    fontSize: "14px",
    "& fieldset": { borderColor: "#E5E7EB" },
    "&:hover fieldset": { borderColor: "#c9c3f0" },
    "&.Mui-focused fieldset": { borderColor: "#7C3AED" },
  },
  "& .MuiInputBase-input": { padding: "12px 14px" },
  "& .MuiInputLabel-root": { fontSize: "14px", color: "#9CA3AF" },
  "& .MuiInputLabel-root.Mui-focused": { color: "#7C3AED" },
};

const TalkToOurConsultantPopupV3 = ({ open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [gclid, setGclid] = useState("");
  const [formError, setFormError] = useState("");
  const [formValues, setFormValues] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    website: window.location.href.slice(0, 250),
    description: window.location.href.includes("full-stack-web-development")
      ? "Full Stack Web Development"
      : window.location.href.includes("devops-and-cloud-training-online")
      ? "DevOps and Cloud with GenAI Master Program"
      : "DevOps and Cloud with GenAI Job Ready Program",
  });

  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });

  const validateFirstName = (v) => (!v ? "First name is required" : "");
  const validateLastName = (v) => (!v ? "Last name is required" : "");
  const validateEmail = (v) => {
    if (!v) return "Email is required";
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(v) ? "" : "Invalid email address";
  };
  const validatePhone = (v) => {
    if (!v) return "Phone number is required";
    return /^\d{10}$/.test(v) ? "" : "Phone number must be 10 digits";
  };

  const handleChange = (e, field) => {
    const value = e.target.value;

    setFormValues((p) => ({ ...p, [field]: value }));

    let error = "";
    switch (field) {
      case "firstName":
        error = validateFirstName(value);
        break;
      case "lastName":
        error = validateLastName(value);
        break;
      case "email":
        error = validateEmail(value);
        break;
      case "phone":
        error = validatePhone(value);
        break;
      default:
        break;
    }

    setErrors((p) => ({ ...p, [field]: error }));
  };

  useEffect(() => {
    const urlGclid = new URLSearchParams(window.location.search).get("gclid");
    if (urlGclid) {
      localStorage.setItem("gclid", urlGclid);
      setGclid(urlGclid);
    } else {
      setGclid(localStorage.getItem("gclid"));
    }
  }, []);

  const handleFormSubmit = (e) => {
    e.preventDefault();

    const firstNameError = validateFirstName(formValues.firstName);
    const lastNameError = validateLastName(formValues.lastName);
    const emailError = validateEmail(formValues.email);
    const phoneError = validatePhone(formValues.phone);

    if (firstNameError || lastNameError || emailError || phoneError) {
      setErrors({
        firstName: firstNameError,
        lastName: lastNameError,
        email: emailError,
        phone: phoneError,
      });
      setFormError(firstNameError || lastNameError || emailError || phoneError);
      return;
    }

    document.forms["WebToLeads774394000000936079"].submit();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: "16px",
          background: "transparent",
          p: 0,
          overflow: "visible",
          maxWidth: 760,
          boxShadow: "0 24px 60px rgba(76,29,149,0.28)",
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* White card */}
        <Box
          sx={{
            position: "relative",
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            background: "#fff",
            borderRadius: "16px",
            minHeight: { xs: "auto", sm: 500 },
            boxShadow: "0 20px 45px rgba(76,29,149,0.18)",
            overflow: "hidden",
          }}
        >
          <IconButton
            aria-label="Close Consultant Form"
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 12,
              top: 12,
              zIndex: 5,
              width: 30,
              height: 30,
              background: "rgba(255,255,255,0.9)",
              color: "#374151",
              "&:hover": { background: "#ffffff" },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>

          {!isMobile && (
            <Box
              sx={{
                position: "relative",
                flex: "0 0 40%",
                background: "#F3EEFE",
              }}
            >
              <img
                loading="lazy"
                src={SupportImage}
                alt="Talk to a Career Coach"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  objectPosition: "60% center",
                }}
              />
            </Box>
          )}

          <Box
            sx={{
              flex: 1,
              p: { xs: 3, sm: "28px 26px" },
              display: "flex",
              flexDirection: "column",
              gap: "14px",
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 25 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
            >
              <Typography
                sx={{
                  fontFamily: "'Poppins', sans-serif",
                  fontWeight: 800,
                  fontSize: { xs: "18px", sm: "20px" },
                  color: "#1F2937",
                  mb: "4px",
                }}
              >
                Talk to a Career Coach
              </Typography>
              <Typography sx={{ fontSize: "13px", color: "#6B7280", mb: "14px" }}>
                15 minutes, zero obligation — real answers about your next move.
              </Typography>

              <form
                id="webform774394000000936079"
                action="https://crm.zoho.in/crm/WebToLeadForm"
                name="WebToLeads774394000000936079"
                method="POST"
                onSubmit={handleFormSubmit}
                acceptCharset="UTF-8"
              >
                <input type="hidden" name="xnQsjsdp" value="1cad0140c50fbd72c59b39201ba5b81ab62e6995c725adf26f6aad1b8616e985" />
                <input type="hidden" name="xmIwtLD" value="2ac5e1702266b95f1281b8514c7dbe8cf46c2552f19b5adf6e506fc723eb7b83fff3cbccd67c12c9418dcb51b5fcb9b1" />
                <input type="hidden" name="actionType" value="TGVhZHM=" />
                <input type="hidden" name="returnURL" value="https://paperlive.in/thankyou" />
                <input type="hidden" id="zc_gad" name="zc_gad" value={gclid} />

                <Box sx={{ display: "grid", gap: "12px" }}>
                  <Box display="flex" gap="10px" flexDirection={isMobile ? "column" : "row"}>
                    <TextField
                      label="First Name"
                      id="First_Name"
                      name="First Name"
                      fullWidth
                      value={formValues.firstName}
                      onChange={(e) => handleChange(e, "firstName")}
                      error={!!errors.firstName}
                      sx={{ ...inputStyle, flex: 1 }}
                    />

                    <TextField
                      label="Last Name"
                      id="Last_Name"
                      name="Last Name"
                      fullWidth
                      value={formValues.lastName}
                      onChange={(e) => handleChange(e, "lastName")}
                      error={!!errors.lastName}
                      sx={{ ...inputStyle, flex: 1 }}
                    />
                  </Box>

                  <TextField
                    label="Email"
                    id="Email"
                    name="Email"
                    type="email"
                    fullWidth
                    value={formValues.email}
                    onChange={(e) => handleChange(e, "email")}
                    error={!!errors.email}
                    sx={inputStyle}
                  />

                  <TextField
                    label="Phone Number"
                    id="Phone"
                    name="Phone"
                    type="tel"
                    fullWidth
                    value={formValues.phone}
                    onChange={(e) => handleChange(e, "phone")}
                    error={!!errors.phone}
                    sx={inputStyle}
                  />

                  {/* Hidden extra fields */}
                  <input type="hidden" id="Website" name="Website" value={formValues.website} />
                  <input type="hidden" id="Description" name="Description" value={formValues.description} />

                  {formError && (
                    <div style={{ color: "red", fontSize: "13px" }}>{formError}</div>
                  )}

                  <Button
                    aria-label="Submit Talk to Our Consultant Form"
                    variant="contained"
                    fullWidth
                    type="submit"
                    sx={{
                      mt: "2px",
                      borderRadius: "9px",
                      py: "12px",
                      fontFamily: "'Poppins', sans-serif",
                      fontWeight: 700,
                      fontSize: "14px",
                      letterSpacing: "0.3px",
                      background: "#7C3AED",
                      boxShadow: "none",
                      ":hover": { background: "#6d28d9", boxShadow: "none" },
                    }}
                  >
                    BOOK MY CALL
                  </Button>
                </Box>
              </form>
            </motion.div>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default TalkToOurConsultantPopupV3;
